import {
  edgesForNode,
  ICollaborationActivityRef as ISharedCollaborationActivityRef,
  ICollaborationGraph as ISharedCollaborationGraph,
  ICollaborationGraphEdge as ISharedCollaborationGraphEdge,
  ICollaborationGraphNode as ISharedCollaborationGraphNode,
  IInstitution as IAaNgInstitution,
  IPerson as IAaNgPerson,
  IPersonsCollaborations,
  toCollaborationActivityRefs as sharedToCollaborationActivityRefs,
} from 'aa-ng';

import { ActivityType } from './enums';
import { IPerson, mapPerson } from './person';

export {
  CollaborationGraphDefaults,
  edgesForNode,
  ICollaborationGraphNode,
  ICollaborationGraphRequest,
} from 'aa-ng';

import { ICollaborationGraphRequest } from 'aa-ng';

/**
 * The by-topic request as this app sends it.
 *
 * Extends the shared contract with the unit scope, which is an EDS concept: the dashboard draws the same
 * topic graph as the Networks tab, seeded from one unit's scholars. Membership is resolved server-side
 * from the id, so a unit of any size costs the same on the wire.
 */
export interface IEdsCollaborationGraphRequest
  extends ICollaborationGraphRequest {
  /** Omitted on the institution dashboard, where the scope is the whole institution group. */
  seedUnitId?: number;
  seedUnitInstitutionId?: number;
}

/**
 * Where the graph controls start, matching AMS so the same topic draws the same graph in both apps.
 *
 * Deliberately below the library's `CollaborationGraphDefaults` (50/50), which is what the *server*
 * falls back to when a caller states nothing. Since these controls always send a value, that
 * fallback never applies here - these are the numbers that decide what a user actually first sees.
 *
 * `topScholarLimit` is the expensive one: every seed brings up to `collaboratorsPerScholar` people
 * and all their edges, so it multiplies first-paint cost on a broad topic. It is also a no-op on any
 * seeded graph (profile, relevancy) - there the caller has already named the seed.
 */
export const EdsCollaborationGraphDefaults = {
  topScholarLimit: 5,
  collaboratorsPerScholar: 25,
} as const;

/**
 * The collaboration graph contract now lives in `aa-ng`, shared with AMS — both apps call the same
 * `collaboration-graph` endpoints and had drifted into separate copies of the same interfaces.
 *
 * It replaced the `IPersonCollaborations[]` payload, which shipped every node's years, units, title
 * and per-type counts up front so a node click could be answered without a second request. What a
 * click actually needs is the ids of the works behind the clicked edge, and those still ride along.
 *
 * This file binds the shared generics to this app's own `ActivityType`. The library takes the enum
 * as a type parameter because TypeScript's numeric enums are nominal — this app's
 * `ActivityType.Article` is not assignable to another declaration of it even though the values
 * agree. Binding here keeps every call site exactly as strongly typed as it was.
 */
export type ICollaborationGraph = ISharedCollaborationGraph<ActivityType>;
export type ICollaborationGraphEdge =
  ISharedCollaborationGraphEdge<ActivityType>;
export type ICollaborationActivityRef =
  ISharedCollaborationActivityRef<ActivityType>;

/**
 * One row of the scholar profile's Collaborators list.
 *
 * Built from the graph rather than from the old wire payload, so the list no longer depends on
 * fields that moved behind the detail call. Keyed on the guid: the numeric id means a single-tenant
 * person for internal collaborators and a faculty-profile person for external ones.
 */
export interface ICollaboratorListItem {
  guid: string;
  personId: number;
  firstName: string;
  middleName: string;
  lastName: string;
  primaryUnitBaseName?: string;

  /** How many works this person shares with the profile, across all activity types. */
  totalCollaborationsCount: number;

  summaries: {
    activityType: ActivityType;
    numberOfCollaborations: number;
  }[];
}

/**
 * The Collaborators list beside the profile graph: everybody who shares a work with the profile,
 * by surname, with their per-type counts.
 */
export function toCollaboratorList(
  graph: ICollaborationGraph,
  profileGuid: string,
): ICollaboratorListItem[] {
  const edges = graph?.edges ?? [];

  return (graph?.nodes ?? [])
    .filter((node) => node.id !== profileGuid)
    .map((node) => {
      // Only edges to the profile itself: this list describes each person's relationship with the
      // scholar being viewed, not their standing in the whole graph.
      const sharedWithProfile = edges.filter(
        (edge) =>
          (edge.source === node.id && edge.target === profileGuid) ||
          (edge.target === node.id && edge.source === profileGuid),
      );

      return {
        guid: node.id,
        personId: node.personId,
        firstName: node.firstName,
        middleName: node.middleName,
        lastName: node.lastName,
        primaryUnitBaseName: node.primaryUnitAffiliation?.baseName,
        totalCollaborationsCount: sharedWithProfile.reduce(
          (total, edge) => total + edge.weight,
          0,
        ),
        summaries: sharedWithProfile
          .map((edge) => ({
            activityType: edge.activityType,
            numberOfCollaborations: edge.weight,
          }))
          .sort((a, b) =>
            ActivityType[a.activityType].localeCompare(
              ActivityType[b.activityType],
            ),
          ),
      };
    })
    .sort((a, b) => a.lastName.localeCompare(b.lastName));
}

/**
 * Adapts the narrow graph to what `aa-collaborative-network` consumes.
 *
 * The library component is deliberately left alone: two out-of-scope dashboard widgets render
 * through the same component with the fat payload, so widening its input would reach
 * them. Mapping here keeps the change inside this app.
 *
 * Every node carries a real guid, so the component's per-graph `graphUsesGuidKeys` check picks the
 * guid key space rather than falling back to numeric ids.
 */
export function mapCollaborationGraph(
  graph: ICollaborationGraph,
): IPersonsCollaborations[] {
  const nodes = graph?.nodes ?? [];
  const edges = graph?.edges ?? [];
  const personIdsByGuid = new Map(
    nodes.map((node) => [node.id, node.personId]),
  );

  return nodes.map((node) => {
    const incident = edges.filter(
      (edge) => edge.source === node.id || edge.target === node.id,
    );

    return {
      personId: node.personId,
      guid: node.id,
      firstName: node.firstName,
      middleName: node.middleName,
      lastName: node.lastName,
      numberOfActivities: node.weight,
      primaryUnitId: node.primaryUnitAffiliation?.id,
      primaryUnitName: node.primaryUnitAffiliation?.name,
      primaryInstitution: {
        id: node.primaryInstitutionId,
        aaId: node.primaryInstitutionAaId,
        name: node.primaryInstitutionName,
      } as IAaNgInstitution,

      // The server emits one edge per pair; the component expects each person to list their own
      // side of it, and dedupes the reciprocal links it then builds by checking a link key and its
      // inverse.
      collaborations: incident.map((edge) => {
        const otherGuid = edge.source === node.id ? edge.target : edge.source;

        return {
          collaboratorPersonId: personIdsByGuid.get(otherGuid),
          collaboratorPersonGuid: otherGuid,
          activityTypeId: edge.activityType,
          numberOfCollaborations: edge.weight,
          activityYears: edge.activityYears,
          activityIds: edge.activityIds,
        };
      }),
    } as IPersonsCollaborations;
  });
}

/**
 * The works the clicked node shares, as the activity drill-down wants them.
 *
 * `personGuid` is the collaborator at the *other* end of each edge — the server narrows each
 * returned activity to those contributors. Pass `collaboratorGuid` to restrict to a single pair,
 * which is what a click on an edge rather than on a node means.
 */
export function toCollaborationActivityRefs(
  graph: ICollaborationGraph,
  nodeGuid: string,
  collaboratorGuid?: string,
): ICollaborationActivityRef[] {
  return sharedToCollaborationActivityRefs(
    nodeGuid,
    edgesForNode(graph, nodeGuid, collaboratorGuid),
  );
}

/**
 * A graph node as an `IPerson`, for the dialogs that show "who you clicked".
 *
 * Shared because three surfaces need it and the graph is now the only place the answer lives: the
 * activity drill-down deliberately returns works with no contributors, so a collaborator cannot be
 * recovered from the activities the way it once was.
 *
 * Only the fields those dialogs read are set; the cast is the acknowledgement that a node is not a
 * whole person.
 */
export function toCollaboratorPerson(
  node: ISharedCollaborationGraphNode,
): IAaNgPerson {
  return mapPerson({
    id: node?.personId,
    guid: node?.id,
    firstName: node?.firstName,
    middleName: node?.middleName,
    lastName: node?.lastName,
    primaryUnitAffiliation: node?.primaryUnitAffiliation,
    primaryInstitution: {
      id: node?.primaryInstitutionId,
      aaId: node?.primaryInstitutionAaId,
      name: node?.primaryInstitutionName,
    },
  } as unknown as IPerson);
}
